import AsyncStorage from '@react-native-async-storage/async-storage';

class BusinessProfileApiService {
  constructor() {
    this.baseUrl = 'https://api.jobeng.ai'; // Replace with your actual API URL
    this.mockMode = true; // Set to false when you have a real backend
  }

  // Simulate API delay
  delay(ms = 1000) {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  // Mock company profile data
  getMockCompanyProfile() {
    return {
      id: 'company_001',
      name: 'JOBENG Construction Ltd',
      industry: 'Construction & Engineering',
      headquarters: 'London, United Kingdom',
      founded: '2015',
      totalEmployees: 85,
      activeProjects: 15,
      annualRevenue: '£2.4M',
      logo: null, // Will be set when user uploads
      description: 'JOBENG Construction Ltd is a leading construction and engineering company specializing in commercial, residential, and infrastructure projects. With over 8 years of experience, we deliver high-quality construction solutions while maintaining the highest safety standards and environmental responsibility.',
      website: 'https://www.jobeng.ai',
      email: 'info@jobeng.ai',
      phone: '+44 20 7123 4567',
      specializations: [
        'Commercial Construction',
        'Residential Development',
        'Infrastructure Projects',
        'Green Building Solutions',
        'Project Management',
        'Safety Consulting'
      ],
      statistics: {
        projectsCompleted: 127,
        clientRetentionRate: 92,
        onTimeDelivery: 96,
        budgetAccuracy: 94,
        safetyRecord: 99.2,
        clientSatisfaction: 4.8
      },
      locations: [
        {
          id: 'loc_001',
          name: 'London Headquarters',
          address: '123 Construction Way, London EC1A 1BB, United Kingdom',
          employees: 45,
          isHeadquarters: true,
          phone: '+44 20 7123 4567'
        },
        {
          id: 'loc_002',
          name: 'Manchester Office',
          address: '456 Building Street, Manchester M1 1AA, United Kingdom',
          employees: 20,
          isHeadquarters: false,
          phone: '+44 161 234 5678'
        },
        {
          id: 'loc_003',
          name: 'Birmingham Branch',
          address: '789 Development Road, Birmingham B1 1BB, United Kingdom',
          employees: 15,
          isHeadquarters: false,
          phone: '+44 121 345 6789'
        },
        {
          id: 'loc_004',
          name: 'Leeds Office',
          address: '321 Project Avenue, Leeds LS1 1CC, United Kingdom',
          employees: 5,
          isHeadquarters: false,
          phone: '+44 113 456 7890'
        }
      ],
      certifications: [
        {
          id: 'cert_001',
          name: 'ISO 9001:2015',
          description: 'Quality Management Systems certification ensuring consistent quality in all our construction projects.',
          issuer: 'British Standards Institution',
          year: '2023',
          expiryDate: '2026-12-31'
        },
        {
          id: 'cert_002',
          name: 'OHSAS 18001',
          description: 'Occupational Health and Safety Management Systems certification demonstrating our commitment to worker safety.',
          issuer: 'Lloyd\'s Register',
          year: '2022',
          expiryDate: '2025-06-30'
        },
        {
          id: 'cert_003',
          name: 'LEED Certified',
          description: 'Leadership in Energy and Environmental Design certification for sustainable building practices.',
          issuer: 'U.S. Green Building Council',
          year: '2023',
          expiryDate: '2028-03-15'
        },
        {
          id: 'cert_004',
          name: 'CHAS Approved',
          description: 'Contractors Health and Safety Assessment Scheme approval for health and safety compliance.',
          issuer: 'CHAS',
          year: '2024',
          expiryDate: '2025-01-31'
        }
      ],
      awards: [
        {
          id: 'award_001',
          name: 'Construction Excellence Award 2023',
          description: 'Recognized for outstanding quality and innovation in commercial construction projects.',
          issuer: 'UK Construction Industry Awards',
          year: '2023'
        },
        {
          id: 'award_002',
          name: 'Safety Leadership Award',
          description: 'Awarded for maintaining exceptional safety standards across all construction sites.',
          issuer: 'Health and Safety Executive',
          year: '2022'
        },
        {
          id: 'award_003',
          name: 'Sustainable Building Award',
          description: 'Recognition for commitment to environmentally responsible construction practices.',
          issuer: 'Green Building Council',
          year: '2023'
        }
      ],
      news: [
        {
          id: 'news_001',
          title: 'JOBENG Wins Major Commercial Development Contract',
          summary: 'We are excited to announce that JOBENG Construction has been awarded the contract for the new £15M commercial development in Central London.',
          date: '2024-01-15',
          category: 'Business'
        },
        {
          id: 'news_002',
          title: 'New Safety Training Program Launched',
          summary: 'JOBENG introduces comprehensive safety training program for all employees, reinforcing our commitment to workplace safety.',
          date: '2024-01-10',
          category: 'Safety'
        },
        {
          id: 'news_003',
          title: 'Expansion to Birmingham Office',
          summary: 'JOBENG Construction expands operations with the opening of a new office in Birmingham, creating 15 new jobs.',
          date: '2024-01-05',
          category: 'Expansion'
        },
        {
          id: 'news_004',
          title: 'Green Building Certification Achieved',
          summary: 'JOBENG achieves LEED certification, demonstrating our commitment to sustainable construction practices.',
          date: '2023-12-20',
          category: 'Sustainability'
        }
      ]
    };
  }

  // Get company profile
  async getCompanyProfile() {
    try {
      console.log('📊 Loading company profile...');
      
      if (this.mockMode) {
        await this.delay(800);
        
        // Try to get saved profile from storage
        const savedProfile = await AsyncStorage.getItem('company_profile');
        let profile = this.getMockCompanyProfile();
        
        if (savedProfile) {
          const parsedProfile = JSON.parse(savedProfile);
          // Merge saved data with mock data
          profile = { ...profile, ...parsedProfile };
        }
        
        console.log('✅ Company profile loaded successfully');
        return {
          success: true,
          data: profile,
          message: 'Company profile loaded successfully'
        };
      }

      // Real API call would go here
      const response = await fetch(`${this.baseUrl}/api/business/profile`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${await this.getAuthToken()}`
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      return {
        success: true,
        data: data,
        message: 'Company profile loaded successfully'
      };
    } catch (error) {
      console.error('❌ Error loading company profile:', error);
      
      // Fallback to mock data on error
      if (!this.mockMode) {
        console.log('🔄 Falling back to mock data...');
        const profile = this.getMockCompanyProfile();
        return {
          success: true,
          data: profile,
          message: 'Company profile loaded (offline mode)'
        };
      }
      
      return {
        success: false,
        data: null,
        message: error.message || 'Failed to load company profile'
      };
    }
  }

  // Update company profile
  async updateCompanyProfile(profileData) {
    try {
      console.log('💾 Updating company profile...', profileData);
      
      if (this.mockMode) {
        await this.delay(1000);
        
        // Save to AsyncStorage
        await AsyncStorage.setItem('company_profile', JSON.stringify(profileData));
        
        console.log('✅ Company profile updated successfully');
        return {
          success: true,
          data: profileData,
          message: 'Company profile updated successfully'
        };
      }

      // Real API call would go here
      const response = await fetch(`${this.baseUrl}/api/business/profile`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${await this.getAuthToken()}`
        },
        body: JSON.stringify(profileData)
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      return {
        success: true,
        data: data,
        message: 'Company profile updated successfully'
      };
    } catch (error) {
      console.error('❌ Error updating company profile:', error);
      return {
        success: false,
        data: null,
        message: error.message || 'Failed to update company profile'
      };
    }
  }

  // Upload company logo
  async uploadCompanyLogo(formData) {
    try {
      console.log('📷 Uploading company logo...');
      
      if (this.mockMode) {
        await this.delay(2000); // Simulate upload time
        
        // In mock mode, we'll just return the local URI
        // In real implementation, this would upload to your server/cloud storage
        const mockLogoUrl = `https://api.jobeng.ai/uploads/logos/company_${Date.now()}.jpg`;
        
        // Save logo URL to profile
        const savedProfile = await AsyncStorage.getItem('company_profile');
        let profile = this.getMockCompanyProfile();
        
        if (savedProfile) {
          profile = { ...profile, ...JSON.parse(savedProfile) };
        }
        
        profile.logo = mockLogoUrl;
        await AsyncStorage.setItem('company_profile', JSON.stringify(profile));
        
        console.log('✅ Company logo uploaded successfully');
        return {
          success: true,
          data: {
            logoUrl: mockLogoUrl,
            message: 'Logo uploaded successfully'
          },
          message: 'Company logo uploaded successfully'
        };
      }

      // Real API call would go here
      const response = await fetch(`${this.baseUrl}/api/business/profile/logo`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${await this.getAuthToken()}`,
          // Don't set Content-Type for FormData, let the browser set it
        },
        body: formData
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      return {
        success: true,
        data: data,
        message: 'Company logo uploaded successfully'
      };
    } catch (error) {
      console.error('❌ Error uploading company logo:', error);
      return {
        success: false,
        data: null,
        message: error.message || 'Failed to upload company logo'
      };
    }
  }

  // Delete company logo
  async deleteCompanyLogo() {
    try {
      console.log('🗑️ Deleting company logo...');
      
      if (this.mockMode) {
        await this.delay(500);
        
        // Remove logo from saved profile
        const savedProfile = await AsyncStorage.getItem('company_profile');
        let profile = this.getMockCompanyProfile();
        
        if (savedProfile) {
          profile = { ...profile, ...JSON.parse(savedProfile) };
        }
        
        profile.logo = null;
        await AsyncStorage.setItem('company_profile', JSON.stringify(profile));
        
        console.log('✅ Company logo deleted successfully');
        return {
          success: true,
          data: null,
          message: 'Company logo deleted successfully'
        };
      }

      // Real API call would go here
      const response = await fetch(`${this.baseUrl}/api/business/profile/logo`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${await this.getAuthToken()}`
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      return {
        success: true,
        data: null,
        message: 'Company logo deleted successfully'
      };
    } catch (error) {
      console.error('❌ Error deleting company logo:', error);
      return {
        success: false,
        data: null,
        message: error.message || 'Failed to delete company logo'
      };
    }
  }

  // Get company statistics
  async getCompanyStatistics() {
    try {
      console.log('📈 Loading company statistics...');
      
      if (this.mockMode) {
        await this.delay(600);
        
        const statistics = {
          totalEmployees: 85,
          totalProjects: 127,
          activeProjects: 15,
          completedProjects: 112,
          totalRevenue: 2400000, // £2.4M
          monthlyGrowth: 12.5,
          clientSatisfaction: 4.8,
          safetyRecord: 99.2,
          onTimeDelivery: 96,
          budgetAccuracy: 94
        };
        
        console.log('✅ Company statistics loaded successfully');
        return {
          success: true,
          data: statistics,
          message: 'Company statistics loaded successfully'
        };
      }

      // Real API call would go here
      const response = await fetch(`${this.baseUrl}/api/business/statistics`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${await this.getAuthToken()}`
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      return {
        success: true,
        data: data,
        message: 'Company statistics loaded successfully'
      };
    } catch (error) {
      console.error('❌ Error loading company statistics:', error);
      return {
        success: false,
        data: null,
        message: error.message || 'Failed to load company statistics'
      };
    }
  }

  // Get authentication token
  async getAuthToken() {
    try {
      const token = await AsyncStorage.getItem('auth_token');
      return token || 'mock_token_12345';
    } catch (error) {
      console.error('Error getting auth token:', error);
      return 'mock_token_12345';
    }
  }

  // Validate image file
  validateImageFile(file) {
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif'];
    const maxSize = 5 * 1024 * 1024; // 5MB

    if (!allowedTypes.includes(file.type)) {
      return {
        valid: false,
        message: 'Invalid file type. Please select a JPEG, PNG, or GIF image.'
      };
    }

    if (file.size > maxSize) {
      return {
        valid: false,
        message: 'File size too large. Please select an image smaller than 5MB.'
      };
    }

    return {
      valid: true,
      message: 'File is valid'
    };
  }

  // Set mock mode
  setMockMode(enabled) {
    this.mockMode = enabled;
    console.log(`🔧 Mock mode ${enabled ? 'enabled' : 'disabled'}`);
  }

  // Clear cached data
  async clearCache() {
    try {
      await AsyncStorage.removeItem('company_profile');
      console.log('🧹 Cache cleared successfully');
      return true;
    } catch (error) {
      console.error('Error clearing cache:', error);
      return false;
    }
  }
}

// Export singleton instance
const businessProfileApiService = new BusinessProfileApiService();
export default businessProfileApiService;
